import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Platform, RefreshControl, ScrollView, StyleSheet, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import useRequireAuth from '@/hooks/useRequireAuth';
import MetaHead from '@/components/MetaHead';
import Text from '@/components/Typography/Text';
import StatusContainer from '@/components/StatusContainer';
import ToolGrid from '@/components/Tools/ToolGrid';
import ToolListItem, { ToolListItemPlaceholder } from '@/components/Tools/ToolListItem';
import LoadMoreButton from '@/components/Buttons/LoadMoreButton';
import CatalogCategoryDropdown from '@/components/Dropdowns/CatalogCategoryDropdown';
import CatalogSortDropdown from '@/components/Catalog/CatalogSortDropdown';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import ToolsHero from '@/components/Tools/ToolsHero';
import ToolsSpotlight from '@/components/Tools/ToolsSpotlight';
import CatalogBrowseShell, { CATALOG_VIEW_MODES } from '@/components/Catalog/CatalogBrowseShell';
import CatalogMobileToolbar from '@/components/Catalog/CatalogMobileToolbar';
import AppDashboardFooter from '@/components/AppDashboardFooter';
import { CATEGORY_COLORS } from '@/components/Tools/ToolCard';
import { paletteForSlug } from '@/utils/agent_catalog';
import { useToolStore, useAuthStore } from '@/store';
import { toolApi as toolApiSsr } from '@/lib/toolApi';

// Module-level cache for build-time SEO data. Populated by `getStaticTools()`
// the first time the page renders during static export (and reused by the
// JSON-LD ItemList so crawlers see the catalog enumerated in the static
// HTML, not just after JS hydration). Empty array on the client.
let _staticToolsCache = null;
async function getStaticTools() {
  if (_staticToolsCache) return _staticToolsCache;
  try {
    const data = await toolApiSsr.listTools({ limit: 200 });
    _staticToolsCache = data.tools || [];
  } catch {
    _staticToolsCache = [];
  }
  return _staticToolsCache;
}

// Pre-resolve catalog at module load so the JSON-LD ItemList below is
// populated when expo-router serializes this page during static export.
if (typeof window === 'undefined') {
  getStaticTools().catch(() => {});
}

const VIEW_MODES = CATALOG_VIEW_MODES;

const TOOLS_CATALOG_SCROLL_KEY = 'gab_tools_catalog_scroll_y';

// Stable accent color per category. Named slugs hit the same palette as
// the tool cards so a "create" rail row matches a "create" card badge;
// any other slug falls back to a hashed color so newly-added categories
// still get a stable color without code changes.
const HASH_PALETTE = [
  '#8B5CF6', '#3B82F6', '#22C55E', '#F59E0B',
  '#EC4899', '#F97316', '#14B8A6', '#06B6D4',
  '#A855F7', '#10B981',
];
/**
 * Foreground + row tint for tool category rail rows.
 * Uses `CATEGORY_COLORS.*.bg` for known slugs so we never append hex alpha
 * to an `rgba(...)` string (which breaks RN/WC and washes text into the row).
 */
function toolCategoryRailAccent(slug) {
  if (!slug) {
    const d = CATEGORY_COLORS.default;
    return { fg: d.fg, rowBg: d.bg };
  }
  const known = CATEGORY_COLORS[slug];
  if (known?.fg && known?.bg) {
    return { fg: known.fg, rowBg: known.bg };
  }
  let hash = 0;
  const s = String(slug);
  for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
  const hex = HASH_PALETTE[hash % HASH_PALETTE.length];
  return { fg: hex, rowBg: `${hex}1F` };
}

// ─── Styles ────────────────────────────────────────────────────────────────

// `isXS` ⊂ `isSM` ⊂ `isMD` ⊂ `isLG` (each ≤-bound), so style decisions read
// most-narrow-first to keep the cascade unambiguous.
const createStyles = (colors, isXS, isSM, isMD, isXL, insetBottom = 0, hideHeaderBorder = false) => {
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.solid_color_primary,
      borderTopWidth: hideHeaderBorder ? 0 : 1,
      borderTopColor: hideHeaderBorder ? 'transparent' : colors.border_color_secondary,
    },
    scrollContainer: {
      flex: 1,
      width: '100%',
    },
    scrollContent: {
      width: '100%',
      paddingBottom: insetBottom + 40,
    },
    contentWrap: {
      paddingHorizontal: 0,
      maxWidth: isXL ? 1460 : 1240,
      width: '100%',
      alignSelf: 'center',
    },
    spotlightWrap: {
      paddingHorizontal: isXS ? 15 : 20,
      marginTop: isXS ? 22 : 30,
      marginBottom: isXS ? 8 : 12,
    },
    section: {
      marginTop: isXS ? 14 : 20,
    },
    blockHeader: {
      flexDirection: 'row',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      gap: 12,
      marginBottom: 14,
    },
    blockTitle: {
      fontSize: isXS ? 18 : 20,
      letterSpacing: -0.4,
    },
    blockCount: {
      fontVariant: ['tabular-nums'],
    },
    block: {
      width: '100%',
    },
    toolListStack: {
      width: '100%',
      gap: 8,
    },
    recommendSection: {
      marginTop: 4,
    },
    loadMore: {
      marginTop: 16,
      alignSelf: 'center',
      minWidth: 220,
    },
    footerWrap: {
      paddingHorizontal: isXS ? 16 : 28,
    },
  });
};

// ─── Page ──────────────────────────────────────────────────────────────────

// `categorySlug` mirrors the tasks page contract: the path-based route
// `/tools/category/[slug]` renders this same page with the prop set,
// which always wins over a stale `?category=` query string.
export const ToolsPage = React.memo(({ categorySlug: categorySlugProp = null } = {}) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { isXS, isSM, isMD, isXL } = useResponsiveness();
  const router = useRouter();
  const params = useLocalSearchParams();
  const insets = useSafeAreaInsets();
  const [refreshing, setRefreshing] = useState(false);
  const [isNearTop, setIsNearTop] = useState(true);
  const scrollViewRef = useRef(null);
  const scrollYRef = useRef(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState(VIEW_MODES.GRID);
  const [catalogSort, setCatalogSort] = useState('relevant');
  const [showOnlyFavorites, setShowOnlyFavorites] = useState(false);

  const favoriteToolIds = useAuthStore(s => s.user?.favoriteToolIds);
  const hasUser = useAuthStore(s => Boolean(s.user?.id));
  const requireAuth = useRequireAuth();

  const styles = useMemo(
    () => createStyles(colors, isXS, isSM, isMD, isXL, insets.bottom, isNearTop),
    [colors, isXS, isSM, isMD, isXL, insets.bottom, isNearTop]
  );

  const toolsBySlug      = useToolStore(s => s.toolsBySlug);
  const catalogSlugs     = useToolStore(s => s.catalogSlugs);
  const featuredSlugs    = useToolStore(s => s.featuredSlugs);
  const isLoading        = useToolStore(s => s.isLoadingCatalog);
  const isLoadingMore    = useToolStore(s => s.isLoadingMoreCatalog);
  const isFetched        = useToolStore(s => s.isFetchedCatalog);
  const error            = useToolStore(s => s.catalogError);
  const catalogTotal     = useToolStore(s => s.catalogTotal);
  const catalogHasMore   = useToolStore(s => s.catalogHasMore);
  const fetchCatalog     = useToolStore(s => s.fetchCatalog);
  const loadMoreCatalog  = useToolStore(s => s.loadMoreCatalog);
  const fetchFeatured    = useToolStore(s => s.fetchFeatured);
  const categories          = useToolStore(s => s.categories);
  const isFetchedCategories = useToolStore(s => s.isFetchedCategories);
  const isLoadingCategories = useToolStore(s => s.isLoadingCategories);
  const fetchCategories     = useToolStore(s => s.fetchCategories);

  // Resolve the active category from the path prop first, then the
  // legacy `?category=` query. Either form keeps working unchanged.
  const urlCategory = useMemo(() => {
    const raw = params?.category;
    if (Array.isArray(raw)) return raw[0] || null;
    return typeof raw === 'string' && raw ? raw : null;
  }, [params?.category]);
  const activeCategory = categorySlugProp || urlCategory || null;

  const allTools = useMemo(
    () => catalogSlugs.map(slug => toolsBySlug[slug]).filter(Boolean),
    [catalogSlugs, toolsBySlug]
  );

  const featuredTools = useMemo(
    () => featuredSlugs.map(slug => toolsBySlug[slug]).filter(Boolean),
    [featuredSlugs, toolsBySlug]
  );

  const favoriteIdSet = useMemo(
    () => new Set((favoriteToolIds || []).map(String)),
    [favoriteToolIds]
  );

  // Search + category filtering are now server-side (see the effect below
  // that re-fetches the catalog with `q` / `category` whenever they
  // change). The only client-side filter left here is `showOnlyFavorites`
  // because it depends on the authenticated user's `favoriteToolIds` and
  // doesn't have a backend filter today.
  const filteredTools = useMemo(() => {
    let list = allTools;
    if (showOnlyFavorites) {
      list = list.filter(tool => tool?.id != null && favoriteIdSet.has(String(tool.id)));
    }
    return list;
  }, [allTools, showOnlyFavorites, favoriteIdSet]);

  // O(1) lookup keyed by slug → position in the canonical catalog order.
  // Computed once per catalog change so the sort comparators below stay
  // linearithmic instead of quadratic-ish (`Array.indexOf` was previously
  // called inside the comparator, which is O(n) per call).
  const catalogIndexBySlug = useMemo(() => {
    const map = new Map();
    for (let i = 0; i < catalogSlugs.length; i++) map.set(catalogSlugs[i], i);
    return map;
  }, [catalogSlugs]);

  const sortedFilteredTools = useMemo(() => {
    const list = [...filteredTools];
    const catalogIndex = (slug) => {
      const i = catalogIndexBySlug.get(slug);
      return i == null ? Number.MAX_SAFE_INTEGER : i;
    };
    const usageCount = (tool) => Number(tool?.stats?.usageCount) || 0;
    switch (catalogSort) {
      case 'relevant':
        return list.sort((a, b) => catalogIndex(a.slug) - catalogIndex(b.slug));
      case 'popular':
        return list.sort((a, b) => {
          const ua = usageCount(a);
          const ub = usageCount(b);
          if (ub !== ua) return ub - ua;
          return catalogIndex(a.slug) - catalogIndex(b.slug);
        });
      case 'name-asc':
        return list.sort((a, b) =>
          (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
        );
      case 'name-desc':
        return list.sort((a, b) =>
          (b.name || '').localeCompare(a.name || '', undefined, { sensitivity: 'base' })
        );
      case 'category': {
        return list.sort((a, b) => {
          const aHas = typeof a.category === 'object' && a.category?.name;
          const bHas = typeof b.category === 'object' && b.category?.name;
          if (aHas && !bHas) return -1;
          if (!aHas && bHas) return 1;
          if (!aHas && !bHas) {
            return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
          }
          const byCat = a.category.name.localeCompare(b.category.name, undefined, { sensitivity: 'base' });
          if (byCat !== 0) return byCat;
          return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
        });
      }
      default:
        return list;
    }
  }, [filteredTools, catalogSort, catalogIndexBySlug]);

  // Featured rail + taxonomy load once on mount — neither depends on the
  // active filters, so they don't need to refetch when search / category
  // change.
  useEffect(() => {
    fetchFeatured(8).catch(() => {});
    if (!isFetchedCategories && !isLoadingCategories) {
      fetchCategories().catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Server-side search + category filtering. Re-fetches the catalog every
  // time the user types (with a small debounce so we don't fire on every
  // keystroke) or switches category. The store resets pagination on each
  // call, so "Load more" picks up under the new filters.
  //
  // On the initial mount this also doubles as the first catalog fetch —
  // with empty `searchQuery` and `null` activeCategory it fires
  // `fetchCatalog({})` after a 0ms tick.
  useEffect(() => {
    const trimmed = searchQuery.trim();
    const filters = {};
    if (trimmed) filters.q = trimmed;
    if (activeCategory) filters.category = activeCategory;
    const handle = setTimeout(() => {
      fetchCatalog(filters).catch(() => {});
    }, trimmed ? 280 : 0);
    return () => clearTimeout(handle);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchCatalog is stable
  }, [searchQuery, activeCategory]);

  // After category URL changes, restore vertical scroll on web so switching
  // pills does not jump the viewport back to the hero.
  const catalogScrollRestoreKey = `${categorySlugProp || ''}|${urlCategory || ''}`;
  useLayoutEffect(() => {
    if (Platform.OS !== 'web' || typeof sessionStorage === 'undefined') return;
    let raw;
    try {
      raw = sessionStorage.getItem(TOOLS_CATALOG_SCROLL_KEY);
    } catch {
      return;
    }
    if (raw == null) return;
    try {
      sessionStorage.removeItem(TOOLS_CATALOG_SCROLL_KEY);
    } catch {
      /* ignore */
    }
    const y = parseInt(raw, 10);
    if (!Number.isFinite(y) || y < 0) return;
    requestAnimationFrame(() => {
      scrollViewRef.current?.scrollTo?.({ y, animated: false });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed restore only
  }, [catalogScrollRestoreKey]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const trimmed = searchQuery.trim();
      const filters = {};
      if (trimmed) filters.q = trimmed;
      if (activeCategory) filters.category = activeCategory;
      await Promise.all([fetchCatalog(filters), fetchFeatured(8), fetchCategories()]);
    } finally {
      setRefreshing(false);
    }
  }, [fetchCatalog, fetchFeatured, fetchCategories, searchQuery, activeCategory]);

  // "Load more" handler — paginates forward under the filters last sent
  // to `fetchCatalog` (the store carries them in `catalogActiveFilters`),
  // so search + category context survive the click.
  const handleLoadMore = useCallback(() => {
    loadMoreCatalog().catch(() => {});
  }, [loadMoreCatalog]);

  const handleToolPress = useCallback((tool) => {
    if (tool?.slug) router.push(`/tools/${tool.slug}`);
  }, [router]);

  const handleViewModeChange = useCallback((mode) => {
    setViewMode(mode);
  }, []);

  const isSearching = !!searchQuery.trim();

  const recommendedToolsForEmptySearch = useMemo(() => {
    if (!searchQuery.trim()) return [];

    const limit = isXS ? 4 : 8;
    const seen = new Set();
    const out = [];

    const add = (tool) => {
      const key = tool?.slug || tool?.id || tool?._id;
      if (!tool || !key || seen.has(String(key))) return;
      seen.add(String(key));
      out.push(tool);
    };

    for (const tool of featuredTools) {
      if (out.length >= limit) break;
      add(tool);
    }

    if (out.length < limit) {
      const byUsage = [...allTools].sort(
        (a, b) => (Number(b?.stats?.usageCount) || 0) - (Number(a?.stats?.usageCount) || 0)
      );
      for (const tool of byUsage) {
        if (out.length >= limit) break;
        add(tool);
      }
    }

    return out;
  }, [searchQuery, featuredTools, allTools, isXS]);

  const handleToolsSortSelect = useCallback((sortId) => {
    if (sortId === 'favorites') {
      // Favorites are owner-only — gate the sort scope so anonymous
      // visitors land on the auth CTA instead of an empty grid.
      requireAuth(
        () => { if (!showOnlyFavorites) setShowOnlyFavorites(true); },
        {
          source: 'tools_favorites_sort',
          title: 'Sign in to see your favorite tools',
          subtitle: 'Favorites are tied to your account so they follow you across devices.',
          icon: 'heart',
        }
      );
      return;
    }
    setCatalogSort(sortId);
    if (showOnlyFavorites) setShowOnlyFavorites(false);
  }, [showOnlyFavorites, requireAuth]);

  // Push the URL alongside the local update so each scope is shareable
  // and SEO-friendly via the path-based category routes.
  const handleCategoryChange = useCallback((slug) => {
    if (Platform.OS === 'web' && typeof sessionStorage !== 'undefined') {
      try {
        sessionStorage.setItem(
          TOOLS_CATALOG_SCROLL_KEY,
          String(Math.max(0, Math.floor(scrollYRef.current))),
        );
      } catch {
        /* ignore quota / private mode */
      }
    }
    if (!slug) {
      router.push('/tools');
    } else {
      router.push(`/tools/category/${encodeURIComponent(slug)}`);
    }
  }, [router]);

  // Tool counts per category, computed against the loaded catalog so the
  // numbers in the rail match what the user actually sees on click.
  const categoryToolCounts = useMemo(() => {
    const counts = new Map();
    for (const tool of allTools) {
      const cat = tool?.category;
      const slug = typeof cat === 'string' ? null : cat?.slug;
      if (!slug) continue;
      counts.set(slug, (counts.get(slug) || 0) + 1);
    }
    return counts;
  }, [allTools]);

  const railCategories = useMemo(
    () => (categories || []).filter(c => c?.slug),
    [categories]
  );

  // Catalog JSON-LD — CollectionPage + ItemList of every active tool. The
  // ItemList is built from the build-time cache when available (static
  // export) and the live Zustand store at runtime, so crawlers see the
  // full catalog and humans always see the freshest copy.
  const seoStructuredData = useMemo(() => {
    const cacheList = Array.isArray(_staticToolsCache) ? _staticToolsCache : [];
    const liveList = allTools || [];
    const sourceList = liveList.length ? liveList : cacheList;
    const itemList = sourceList.slice(0, 100).map((tool, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      url: `https://gab.ai/tools/${tool.slug}`,
      name: tool?.meta?.title || tool?.name,
    }));
    return {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'CollectionPage',
          name: 'AI Tools',
          description: 'Specialized single-purpose AI tools for content creation, image generation, video, music, code, analysis, and more.',
          url: 'https://gab.ai/tools',
          isPartOf: { '@type': 'WebSite', name: 'Gab AI', url: 'https://gab.ai' },
          inLanguage: 'en-US',
          mainEntity: { '@type': 'ItemList', itemListElement: itemList },
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://gab.ai' },
            { '@type': 'ListItem', position: 2, name: 'AI Tools', item: 'https://gab.ai/tools' },
          ],
        },
      ],
    };
  }, [allTools]);

  const toolsCatalogSortOptions = useMemo(() => [
    { id: 'relevant', label: t('pages.tools.catalogSortRelevant') || 'Relevant', icon: 'bars-staggered', iosIcon: 'line.3.horizontal.decrease' },
    { id: 'popular', label: t('pages.tools.catalogSortPopular') || 'Popular', icon: 'fire', iosIcon: 'flame' },
    { id: 'name-asc', label: t('pages.tools.catalogSortNameAsc') || 'Name A–Z', icon: 'arrow-down-a-z', iosIcon: 'textformat.abc' },
    { id: 'name-desc', label: t('pages.tools.catalogSortNameDesc') || 'Name Z–A', icon: 'arrow-up-z-a', iosIcon: 'textformat.abc' },
    { id: 'category', label: t('pages.tools.catalogSortCategory') || 'By category', icon: 'layer-group', iosIcon: 'square.stack.3d.up' },
  ], [t]);

  const toolsCategoryDropdownCategories = useMemo(
    () => railCategories.map((cat) => ({
      ...cat,
      count: categoryToolCounts.get(cat.slug) ?? cat?.stats?.toolCount ?? 0,
    })),
    [railCategories, categoryToolCounts],
  );

  const toolsFormatCategoryCount = useCallback((count) => {
    if (typeof count !== 'number' || count <= 0) return null;
    return count === 1 ? '1 tool' : `${count} tools`;
  }, []);

  // "My favorites" is account-bound — only offer it to signed-in users. It
  // renders inside a DropdownMenu.Content, so on native it has to be a raw
  // DropdownMenu.Item — the native menu drops anything else.
  const toolsSortFooter = useMemo(() => {
    if (!hasUser) return null;
    const label = t('pages.tools.myFavorites') || 'My favorites';

    if (Platform.OS !== 'web') {
      return (
        <DropdownMenu.Item key="favorites" onSelect={() => handleToolsSortSelect('favorites')}>
          {showOnlyFavorites && <DropdownMenu.ItemIcon ios={{ name: 'checkmark' }} />}
          <DropdownMenu.ItemTitle>{label}</DropdownMenu.ItemTitle>
        </DropdownMenu.Item>
      );
    }

    return (
      <DropdownMenuItem
        key="favorites"
        icon="heart"
        title={label}
        isActive={showOnlyFavorites}
        onSelect={() => handleToolsSortSelect('favorites')}
      />
    );
  }, [hasUser, t, showOnlyFavorites, handleToolsSortSelect]);

  const toolsDiscoveryHeroSearch = useMemo(() => ({
    value: searchQuery,
    onChangeText: setSearchQuery,
    placeholder: t('pages.tools.searchPlaceholder') || 'Search tools…',
    testID: 'tools-catalog-search',
  }), [searchQuery, t]);

  // Category navigation lives in the hero tiles on desktop, so the toolbar
  // only carries sort. XS keeps a category chip because the tiles scroll
  // off-screen once the user is deep in the grid.
  const toolsBrowseToolbarTrailing = useMemo(() => (
    <CatalogSortDropdown
      value={showOnlyFavorites ? 'favorites' : catalogSort}
      options={toolsCatalogSortOptions}
      onChange={handleToolsSortSelect}
      label={t('pages.tools.catalogSort') || 'Sort'}
      footer={toolsSortFooter}
      extraActiveOptions={hasUser ? [{ id: 'favorites', label: t('pages.tools.myFavorites') || 'My favorites', icon: 'heart' }] : []}
    />
  ), [
    showOnlyFavorites,
    t,
    catalogSort,
    toolsCatalogSortOptions,
    handleToolsSortSelect,
    toolsSortFooter,
    hasUser,
  ]);

  const toolsBrowseToolbarBase = useMemo(() => ({
    viewMode,
    listMode: VIEW_MODES.LIST,
    gridMode: VIEW_MODES.GRID,
    onViewModeChange: handleViewModeChange,
    trailing: toolsBrowseToolbarTrailing,
  }), [viewMode, handleViewModeChange, toolsBrowseToolbarTrailing]);

  const toolsMobileToolbar = useMemo(() => (
    <CatalogMobileToolbar
      viewMode={viewMode}
      onViewModeChange={handleViewModeChange}
      listMode={VIEW_MODES.LIST}
      gridMode={VIEW_MODES.GRID}
      chipSlots={[
        <CatalogCategoryDropdown
          key="tools-cat"
          variant="toolbarChip"
          categories={toolsCategoryDropdownCategories}
          activeCategorySlug={showOnlyFavorites ? null : activeCategory}
          onChangeCategory={(slug) => {
            if (showOnlyFavorites) setShowOnlyFavorites(false);
            handleCategoryChange(slug);
          }}
          allOptionLabel={t('pages.tools.allTools') || 'All tools'}
          defaultTriggerLabel={t('pages.tasks.categoriesEyebrow') || 'Categories'}
          mobileTriggerLabel={t('pages.tasks.categoriesEyebrow') || 'Categories'}
          mobileIconOnly={false}
          getCategoryTint={(slug) => toolCategoryRailAccent(slug).fg}
          formatCountLabel={toolsFormatCategoryCount}
          getCategoryCount={(cat) => cat.count}
        />,
      ]}
    />
  ), [
    viewMode,
    handleViewModeChange,
    toolsCategoryDropdownCategories,
    showOnlyFavorites,
    activeCategory,
    handleCategoryChange,
    t,
    toolsFormatCategoryCount,
  ]);

  const activeCategoryRecord = useMemo(
    () => (activeCategory ? railCategories.find(c => c.slug === activeCategory) : null),
    [activeCategory, railCategories]
  );

  // Hero tiles follow the taxonomy's own `order` so the row reads the way
  // the catalog was designed (Create → Edit → Write → …), not insertion order.
  const heroCategories = useMemo(
    () => [...railCategories].sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0)),
    [railCategories]
  );

  const heroCopy = useMemo(() => {
    if (activeCategory) {
      const name = activeCategoryRecord?.name
        || activeCategory.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
      return {
        eyebrow: t('pages.tools.categoryEyebrow') || 'Tool category',
        headline: name,
        subtitle: activeCategoryRecord?.description || null,
        accentPalette: paletteForSlug(activeCategory),
      };
    }
    return {
      eyebrow: t('pages.tools.heroEyebrow') || 'Gab AI Tools',
      headline: t('pages.tools.heroHeadline') || 'AI tools that do one job well.',
      subtitle: t('pages.tools.heroLead')
        || 'Purpose-built tools for writing, images, video, music, code, and analysis. Fill in a few fields, get a finished result, then keep going in chat.',
      accentPalette: null,
    };
  }, [activeCategory, activeCategoryRecord, t]);

  const heroCountLabel = useMemo(() => {
    if (!isFetched || !catalogTotal) return null;
    return catalogTotal === 1
      ? (t('pages.tools.toolsCountSingle') || '1 tool')
      : (t('pages.tools.toolsCount') || '%COUNT% tools').replace('%COUNT%', String(catalogTotal));
  }, [isFetched, catalogTotal, t]);

  const handleHeroCategoryChange = useCallback((slug) => {
    if (showOnlyFavorites) setShowOnlyFavorites(false);
    handleCategoryChange(slug);
  }, [showOnlyFavorites, handleCategoryChange]);

  const showSpotlight = !isSearching && !activeCategory && !showOnlyFavorites && featuredTools.length > 0;

  // ── Sub-renderers ────────────────────────────────────────────────────────

  const renderToolCollection = (tools) => (
    viewMode === VIEW_MODES.LIST ? (
      <View style={styles.toolListStack}>
        {tools.map(tool => (
          <ToolListItem
            key={tool.id || tool._id || tool.slug}
            tool={tool}
            onPress={handleToolPress}
          />
        ))}
      </View>
    ) : (
      <ToolGrid tools={tools} onToolPress={handleToolPress} />
    )
  );

  const renderCatalog = () => {
    const headerLabel = isSearching
      ? `${t('pages.tasks.resultsFor') || 'Results for'} "${searchQuery.trim()}"`
      : showOnlyFavorites
        ? (t('pages.tools.myFavorites') || 'My favorites')
        : activeCategoryRecord
          ? (t('pages.tools.categoryTools') || '%NAME% tools').replace('%NAME%', activeCategoryRecord.name || activeCategoryRecord.slug)
          : (t('pages.tools.allTools') || 'All tools');

    const showSearchRecommendations = isSearching
      && !showOnlyFavorites
      && !error
      && (isFetched || !isLoading)
      && !sortedFilteredTools.length
      && recommendedToolsForEmptySearch.length > 0;

    // Header count: when favorites filter is on we can only count what's
    // loaded (favorites mask is client-side). Otherwise show the true
    // server total so users know there's more behind "Load more".
    const headerCount = showOnlyFavorites
      ? sortedFilteredTools.length
      : (catalogTotal || sortedFilteredTools.length);

    // "Load more" only shows when the server has more rows AND the user
    // isn't filtering by favorites client-side (that filter would skew the
    // count and `loadMoreCatalog` can't know which extra rows are
    // favourited without loading them all).
    const showLoadMore =
      catalogHasMore
      && !showOnlyFavorites
      && !error
      && !isLoading
      && sortedFilteredTools.length > 0;

    const headerCountLabel = headerCount === 1
      ? (t('pages.tools.toolsCountSingle') || '1 tool')
      : (t('pages.tools.toolsCount') || '%COUNT% tools').replace('%COUNT%', String(headerCount));

    return (
      <View style={styles.block}>
        <View style={styles.blockHeader}>
          <Text tag="h2" weight="extraBold" style={styles.blockTitle} numberOfLines={1}>{headerLabel}</Text>
          {(!isLoading || isFetched) && headerCount > 0 ? (
            <Text size="s" color="tertiary" style={styles.blockCount}>{headerCountLabel}</Text>
          ) : null}
        </View>

        {error && !isLoading && !sortedFilteredTools.length ? (
          <StatusContainer
            type='error'
            message={error}
            actionText={t('pages.common.retry') || 'Retry'}
            onAction={onRefresh}
          />
        ) : isLoading && !isFetched ? (
          viewMode === VIEW_MODES.LIST ? (
            <View style={styles.toolListStack}>
              {[...Array(isXS ? 5 : 8)].map((_, i) => (
                <ToolListItemPlaceholder key={i} />
              ))}
            </View>
          ) : (
            <ToolGrid isLoading placeholderCount={isXS ? 4 : 8} />
          )
        ) : showSearchRecommendations ? (
          <View style={styles.recommendSection}>
            <View style={styles.blockHeader}>
              <Text weight="bold" size="m" style={{ flex: 1, minWidth: 0 }}>
                {t('pages.tools.noSearchResultsRecommend') || 'No results for your search but check these out'}
              </Text>
            </View>
            {renderToolCollection(recommendedToolsForEmptySearch)}
          </View>
        ) : !sortedFilteredTools.length ? (
          <StatusContainer
            type='empty'
            message={
              showOnlyFavorites && !(favoriteToolIds || []).length
                ? (t('pages.tools.noFavoriteToolsYet') || 'Save tools with the heart to see them here.')
                : showOnlyFavorites
                  ? (t('pages.tools.noFavoriteToolsMatch') || 'No saved favorites match this filter.')
                  : isSearching || activeCategory
                    ? (t('pages.tools.noResultsForQuery') || 'No tools match your search.')
                    : (t('pages.tools.noToolsYet') || 'No tools available yet.')
            }
          />
        ) : (
          <>
            {renderToolCollection(sortedFilteredTools)}
            {showLoadMore ? (
              <LoadMoreButton
                onPress={handleLoadMore}
                isLoading={isLoadingMore}
                isLarge
                style={styles.loadMore}
              />
            ) : null}
          </>
        )}
      </View>
    );
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <View style={styles.container}>
      {!categorySlugProp && (
        <MetaHead
          title={t('pages.tools.title') || 'AI Tools — Single-Purpose AI for Every Task'}
          description={t('pages.tools.description') || 'Browse Gab AI\u2019s library of specialized AI tools for writing, image and video generation, music, code, analysis, and more. Fill out a form, get a result, then continue in chat.'}
          canonical="/tools"
          keywords="AI tools, AI utilities, AI generators, AI writing, AI image, AI video, AI music, AI assistants"
          structuredData={seoStructuredData}
        />
      )}
      <ScrollView
        ref={scrollViewRef}
        style={styles.scrollContainer}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        onScroll={(e) => {
          scrollYRef.current = e.nativeEvent.contentOffset.y;
          setIsNearTop(e.nativeEvent.contentOffset.y < 10);
        }}
        scrollEventThrottle={16}
        // Native: tapping a card while the search keyboard is up should fire
        // the card press (default behavior would just dismiss the keyboard
        // and swallow the tap). Dragging the list also dismisses the
        // keyboard naturally — both are baseline iOS expectations.
        keyboardShouldPersistTaps='handled'
        keyboardDismissMode={Platform.OS === 'ios' || Platform.OS === 'android' ? 'on-drag' : 'none'}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={colors.text_color_secondary}
            colors={[colors.text_color_secondary]}
          />
        }
      >
        <View style={styles.contentWrap}>
          <ToolsHero
            eyebrow={heroCopy.eyebrow}
            headline={heroCopy.headline}
            subtitle={heroCopy.subtitle}
            accentPalette={heroCopy.accentPalette}
            countLabel={heroCountLabel}
            search={toolsDiscoveryHeroSearch}
            categories={heroCategories}
            activeCategorySlug={showOnlyFavorites ? null : activeCategory}
            onCategoryChange={handleHeroCategoryChange}
            allLabel={t('pages.tools.allTools') || 'All tools'}
          />

          {showSpotlight ? (
            <View style={styles.spotlightWrap}>
              <ToolsSpotlight
                tools={featuredTools}
                onToolPress={handleToolPress}
                title={t('pages.tools.featured') || 'Featured'}
                subtitle={t('pages.tools.featuredSubtitle') || 'Hand-picked by the Gab AI team'}
              />
            </View>
          ) : null}

          <View style={styles.section}>
            <CatalogBrowseShell
              isXS={isXS}
              bodyGap={18}
              hideRail
              suppressMobilePills
              toolbar={isXS
                ? { ...toolsBrowseToolbarBase, mobile: toolsMobileToolbar }
                : toolsBrowseToolbarBase}
            >
              {renderCatalog()}
            </CatalogBrowseShell>
          </View>

          <View style={styles.footerWrap}>
            <AppDashboardFooter />
          </View>
        </View>
      </ScrollView>
    </View>
  );
});

ToolsPage.displayName = 'ToolsPage';

export default function ToolsRoute() {
  return <ToolsPage />;
}
